import React from "react";
import { BaseCondition } from "@tiny/api";
import { Button, Typography } from "@tiny/components";
import PlusCircleOutline from "@tiny/icons/generated/PlusCircleOutline";
import Condition from "./Condition/Condition";
import { useConditionGroup } from "./ConditionGroup.hooks";
import { useStyles } from "./ConditionGroup.styles";

export interface ConditionGroupProps {
  prefixName: string;
  index: number;
  onEmpty?: (index: number) => void;
  hideDivider?: boolean;
  attributesTypes: NonNullable<BaseCondition["attributeType"]>[];
  maxConditions: number;
  minConditions?: number;
  hideAddConditionsAtMax?: boolean;
}

function ConditionGroup(props: ConditionGroupProps) {
  const { minConditions = 0 } = props;
  const {
    addCondition,
    deleteCondition,
    index,
    segmentConditionsInfo,
    fields,
    prefixName,
    hideDivider,
    attributesTypes,
    editDisabled,
    editDisabledTooltip,
    maxConditions,
    hideAddConditionsAtMax,
  } = useConditionGroup(props);

  const classes = useStyles();

  const addTooltip = editDisabled
    ? editDisabledTooltip
    : `You can add at maximum ${maxConditions} conditions`;

  const notExceedNrOfConditions =
    minConditions < maxConditions && fields.length < maxConditions;

  const shouldShowAddConditions =
    notExceedNrOfConditions || hideAddConditionsAtMax !== true;

  return (
    <section className={classes.root}>
      <div className={classes.conditions}>
        {fields?.map((field, indexCondition) => (
          <Condition
            segmentConditionsInfo={segmentConditionsInfo}
            groupIndex={index}
            key={field.id}
            index={indexCondition}
            onDelete={deleteCondition}
            showDivider={indexCondition < fields.length - 1}
            prefixName={prefixName}
            attributesTypes={attributesTypes}
            disabled={editDisabled}
            deletable={
              minConditions ? indexCondition + 1 > minConditions : undefined
            }
          />
        ))}
      </div>
      <div className={classes.actions}>
        {shouldShowAddConditions && (
          <Button
            variant="text"
            noPadding
            startIcon={<PlusCircleOutline />}
            onClick={addCondition}
            disabled={fields.length >= maxConditions || editDisabled}
            disabledTooltip={addTooltip}
          >
            Add condition
          </Button>
        )}
      </div>
      {!hideDivider && (
        <div className={classes.divider}>
          <Typography variant="label3" color="grey" tone={600}>
            OR
          </Typography>
        </div>
      )}
    </section>
  );
}

export default React.memo(ConditionGroup);
